import { gql } from "@apollo/client";
import { useEffect } from "react";
import { useQuery } from "@apollo/client/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { ExternalLink, FileText } from "lucide-react";

/**
 * Quicklook: the cited page of a document, with the evidence highlighted.
 *
 * The point is that checking a claim should not cost a 12 MB download. The page arrives as
 * a ~70 KB image the proxy serves immutably, and the highlight boxes arrive separately as
 * fractions of the page, so one cached image serves every citation of it.
 *
 * Degrades on purpose, in two directions. A page nobody pre-rendered (16% of cited
 * documents exceed the per-document budget) shows the "open the document" path instead,
 * which is exactly what clicking the citation did before this existed. And an empty rect
 * list is normal rather than broken: PDFs hyphenate across line breaks, so a snippet does
 * not always survive as searchable text. The page still answers the question.
 */

const QUICKLOOK = gql`
  query DocumentQuicklook($documentId: UUID!, $page: Int!, $snippet: String) {
    documentQuicklook(documentId: $documentId, page: $page, snippet: $snippet) {
      page
      pageCount
      pageUrl
      rects {
        x
        y
        w
        h
      }
    }
  }
`;

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface QuicklookPage {
  page: number;
  pageCount: number | null;
  pageUrl: string | null;
  rects: Rect[];
}

interface QuicklookData {
  documentQuicklook: QuicklookPage;
}

/**
 * The cited page's preview, fetched as soon as the card mounts so the card can hide its
 * magnifier when no page was rendered, and the dialog opens with everything in hand. The
 * query is small (URLs and highlight fractions); the page image is warmed here too, so
 * opening shows it at once instead of behind a loading state. Returns null until a
 * rendered page exists. cache-first: the artifacts behind these URLs are immutable.
 */
export function useQuicklookPage(
  documentId: string | null | undefined,
  page: number | null | undefined,
  snippet: string | null | undefined,
): QuicklookPage | null {
  const { data } = useQuery<QuicklookData>(QUICKLOOK, {
    variables: { documentId, page, snippet: snippet ?? null },
    skip: !documentId || !page,
    fetchPolicy: "cache-first",
  });
  const preview = data?.documentQuicklook ?? null;
  const pageUrl = preview?.pageUrl ?? null;
  useEffect(() => {
    if (pageUrl) new Image().src = pageUrl;
  }, [pageUrl]);
  return pageUrl ? preview : null;
}

export interface DocumentQuicklookProps {
  documentId: string;
  /** 1-based, the numbering every citing surface already carries. */
  page: number;
  /** Already fetched by the caller (useQuicklookPage); fetched here when absent. */
  preview?: QuicklookPage | null;
  /** Evidence excerpt shown under the page. Optional. */
  snippet?: string | null;
  documentName?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the full PDF. Always offered, and the only option when no page was rendered. */
  onOpenDocument: () => void;
}

export function DocumentQuicklook({
  documentId,
  page,
  preview: given,
  snippet,
  documentName,
  open,
  onOpenChange,
  onOpenDocument,
}: DocumentQuicklookProps) {
  const fetched = useQuicklookPage(given ? null : documentId, page, snippet);
  const preview = given ?? fetched;

  // No rendered page (yet): offer the document itself, as clicking a citation always did.
  if (!preview) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm font-medium">{documentName ?? "Dokument"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Für diese Seite gibt es noch keine Vorschau.</p>
          <Button variant="secondary" size="sm" onClick={onOpenDocument}>
            <ExternalLink className="h-3 w-3 mr-1" />
            Dokument öffnen
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-full max-h-[88vh] p-0 flex flex-col gap-0">
        <DialogHeader className="p-4 border-b shrink-0">
          <DialogTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4 shrink-0" />
            <span className="truncate">{documentName ?? "Dokument"}</span>
            <span className="text-muted-foreground font-normal shrink-0">
              Seite {preview.page}
              {preview.pageCount ? ` von ${preview.pageCount}` : ""}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-muted/30 p-4">
          {/* The overlay is positioned against the image box, so the rect fractions map
              straight to percentages and stay correct at any rendered width. */}
          <div className="relative mx-auto w-fit shadow-sm">
            <img src={preview.pageUrl!} alt={`Seite ${preview.page}`} className="block max-w-full h-auto bg-white" />
            {preview.rects.map((r, i) => (
              <span
                key={i}
                aria-hidden="true"
                className="absolute pointer-events-none rounded-[1px] bg-primary-accent/25 outline outline-1 outline-primary-accent/60"
                style={{
                  left: `${r.x * 100}%`,
                  top: `${r.y * 100}%`,
                  width: `${r.w * 100}%`,
                  height: `${r.h * 100}%`,
                }}
              />
            ))}
          </div>

          {/* Shown regardless of whether it could be highlighted, so a missed match
              costs the reader nothing. */}
          {snippet && (
            <blockquote className="mx-auto mt-4 max-w-2xl border-l-2 border-primary-accent pl-3 text-xs text-muted-foreground whitespace-pre-line">
              {snippet}
            </blockquote>
          )}
        </div>

        <div className="border-t p-3 shrink-0 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onOpenDocument}>
            <ExternalLink className="h-3 w-3 mr-1" />
            Vollständiges Dokument öffnen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
